import React, { useState, useEffect } from "react";
import { Bookmark, Share2, Copy, Facebook, Twitter, MessageCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import placeholder from "../../assets/Car Image.png"; // Update with actual placeholder image
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import api from "../../lib/api.ts";

const CarHeader = ({ car, auctionLane, vehicleList }) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showShareDropdown, setShowShareDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showShareDropdown && !event.target.closest('.share-dropdown-container')) {
        setShowShareDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareDropdown]);

  // Fetch wishlist on component mount to check if current vehicle is bookmarked
  useEffect(() => {
    const fetchWishlist = async () => {
      if (!user?.id || !car?.id) return;

      try {
        const response = await api.get("/wishlist");
        const wishlistData = response.data?.data || response.data;
        
        if (wishlistData && Array.isArray(wishlistData)) {
          // Check if current vehicle is in wishlist
          const isInWishlist = wishlistData.some(item => item.vehicleId === car.id);
          setIsBookmarked(isInWishlist);
        }
      } catch (error) {
        console.error("Error fetching wishlist:", error);
      }
    };

    fetchWishlist();
  }, [user?.id, car?.id]);

  // Toggle wishlist item
  const toggleWishlist = async (vehicleId) => {
    if (!user?.id) {
      // Redirect to login if not authenticated
      navigate("/login");
      return;
    }

    try {
      const response = await api.post("/wishlist/toggle", { vehicleId });

      if (response.data.success) {
        // Use the action from API response to determine the new state
        const action = response.data.data?.action;
        const isAdded = action === "added";
        setIsBookmarked(isAdded);

        // Show appropriate toast message based on API response
        if (isAdded) {
          showSuccess("Vehicle added to Watch List successfully!");
        } else {
          showSuccess("Vehicle removed from Watch List successfully!");
        }
      } else {
        // Show the specific API error message
        const errorMessage = response.data.message || "Failed to update Watch List. Please try again.";
        showError(errorMessage);
      }
    } catch (error) {
      console.error("Error toggling Watch List:", error);
      // Show specific error message from API response if available
      const errorMessage = error.response?.data?.message || "Failed to update Watch List. Please try again.";
      showError(errorMessage);
    }
  };

  // Share functionality
  const getShareUrl = () => {
    const baseUrl = window.location.origin;
    if (car?.vehicle?.slug) {
      return `${baseUrl}/${car.vehicle.slug}`;
    } 
    return window.location.href;
  };

  const getShareText = () => {
    return `Check out this amazing vehicle: ${car?.name} on Khatal Jazeera Auctions!`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      showSuccess("Link copied to clipboard!");
      setShowShareDropdown(false);
    } catch (error) {
      console.error("Failed to copy:", error);
      showError("Failed to copy link. Please try again.");
    }
  };

  const shareOnFacebook = () => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(getShareText());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`, '_blank', 'width=600,height=400');
    setShowShareDropdown(false);
  };

  const shareOnTwitter = () => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(getShareText());
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank', 'width=600,height=400');
    setShowShareDropdown(false);
  };

  const shareOnWhatsApp = () => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(getShareText());
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
    setShowShareDropdown(false);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: car?.name,
          text: getShareText(),
          url: getShareUrl(),
        });
        setShowShareDropdown(false);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          showError("Failed to share. Please try again.");
        }
      }
    } else {
      // Fallback to copy to clipboard if native share is not supported
      copyToClipboard();
    }
  };

  // Get all vehicles in the lane and sort them by run number
  const allVehicles = React.useMemo(() => {
    let list = [];
    if (vehicleList && vehicleList.length > 0) {
      list = vehicleList;
    } else if (auctionLane?.auctionLaneVehicles) {
      list = auctionLane.auctionLaneVehicles;
    } else if (car?.auctionLanesVehicles) {
      list = car.auctionLanesVehicles;
    }

    return [...list].sort((a, b) => {
      const runA = a.runNo || a.auctionLanesVehicles?.[0]?.runNo || 0;
      const runB = b.runNo || b.auctionLanesVehicles?.[0]?.runNo || 0;
      return runA - runB;
    });
  }, [vehicleList, auctionLane, car]);

  const [previewIndex, setPreviewIndex] = useState(-1);

  // When the current auction car changes, update the preview index to the next car
  useEffect(() => {
    if (allVehicles.length > 0 && car?.id) {
      const currentIdx = allVehicles.findIndex(v => (v.vehicle?.id || v.id) == car.id);
      if (currentIdx !== -1) {
        // Default to next car if available, otherwise stay at current
        const nextIdx = (currentIdx + 1) < allVehicles.length ? currentIdx + 1 : currentIdx;
        setPreviewIndex(nextIdx);
      } else {
        setPreviewIndex(0);
      }
    }
  }, [car?.id, allVehicles]);

  const previewCar = allVehicles[previewIndex];

  const handlePrev = () => {
    if (previewIndex > 0) {
      setPreviewIndex(previewIndex - 1);
    }
  };

  const handleNext = () => {
    if (previewIndex < allVehicles.length - 1) {
      setPreviewIndex(previewIndex + 1);
    }
  };

  const getCarData = (item) => {
    if (!item) return null;
    return item.vehicle || item;
  };

  const previewCarData = getCarData(previewCar);
  const previewRunNo = previewCar?.runNo || previewCar?.auctionLanesVehicles?.[0]?.runNo || 0;

  return (
    <div className="max-w-7xl mx-auto  ">
      {/* <div className="text-center mt-3 mb-2 font-bold">
        <span className="text-[14px] sm:text-[16px] md:text-[18px] lg:text-[24px]">
          {auctionLane?.auction?.name}
          <span className="text-primary">
            {" : " + car?.auctionLanesVehicles?.[0]?.auctionLane?.lane?.name ||
              "Loading..."}
          </span>
        </span>
      </div> */}
      <div className="flex flex-col md:flex-row items-center justify-between py-1 md:py-2 lg:py-2 px-1 md: ">
        {/* -----------------------Left Side--------------------- */}
        <div className="flex flex-col gap-1 sm:gap-1 md:gap-1 lg:gap-1 w-full md:w-auto">
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-[26px] font-bold ">
            {car?.name}
          </h1>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center">
            <div className="text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px] font-bold ">
              Run No:{" "}
              <span className="text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px] font-normal ">
                <span className="font-semibold">
                  {car?.auctionLanesVehicles?.[0]?.runNo}
                </span>
              </span>
            </div>
          </div>

          {/* Watchlist + Share */}
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-1 sm:gap-1">
            <button 
              className="flex items-center"
              onClick={() => toggleWishlist(car?.id)}
            >
              <div className={`${isBookmarked ? 'text-white bg-[#e55a2b]' : 'text-[#e55a2b] bg-white hover:text-white hover:bg-gray-300'} p-2 rounded-full transition-colors duration-200`}>
                <Bookmark size={16} className="h-6 w-6 sm:h-6 sm:w-6" />
              </div>
              <span className="text-[12px] sm:text-[14px] font-normal border-b border-black cursor-pointer">
                {isBookmarked ? 'Remove from Watchlist' : 'Add To Watchlist'}
              </span>
            </button>

            <div className="relative share-dropdown-container">
              <button 
                className="flex items-center hover:text-primary rounded-full"
                onClick={() => setShowShareDropdown(!showShareDropdown)}
              >
                <div className="text-black bg-white hover:text-white p-2 rounded-full hover:bg-gray-300">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 16 17"
                    fill="black"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M11.0003 2C9.80371 2 8.83366 2.97005 8.83366 4.16667C8.83366 4.29727 8.84521 4.42518 8.86736 4.54942L5.7136 6.75705C5.69757 6.76827 5.6824 6.78026 5.66811 6.79293C5.30028 6.50498 4.83701 6.33333 4.33366 6.33333C3.13704 6.33333 2.16699 7.30338 2.16699 8.5C2.16699 9.69662 3.13704 10.6667 4.33366 10.6667C4.83701 10.6667 5.30028 10.495 5.66811 10.2071C5.68241 10.2197 5.69757 10.2317 5.7136 10.243L8.86736 12.4506C8.84521 12.5748 8.83366 12.7027 8.83366 12.8333C8.83366 14.03 9.80371 15 11.0003 15C12.1969 15 13.167 14.03 13.167 12.8333C13.167 11.6367 12.1969 10.6667 11.0003 10.6667C10.2985 10.6667 9.67456 11.0004 9.27861 11.5178L6.29236 9.42743C6.42572 9.14628 6.50033 8.83185 6.50033 8.5C6.50033 8.16815 6.42572 7.85372 6.29236 7.57257L9.27861 5.4822C9.67456 5.99961 10.2985 6.33333 11.0003 6.33333C12.1969 6.33333 13.167 5.36328 13.167 4.16667C13.167 2.97005 12.1969 2 11.0003 2Z"
                      fill="#e55a2b"
                    />
                  </svg>
                </div>
                <span className="text-[12px] sm:text-[14px] font-normal border-b border-black cursor-pointer">
                  Share
                </span>
              </button>

              {/* Share Dropdown */}
              {showShareDropdown && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="py-2">
                    {/* Native Share (Mobile) */}
                    {navigator.share && (
                      <button
                        onClick={handleNativeShare}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Share2 size={16} />
                        Share
                      </button>
                    )}
                    
                    {/* Copy Link */}
                    <button
                      onClick={copyToClipboard}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Copy size={16} />
                      Copy Link
                    </button>
                    
                    {/* Facebook */}
                    <button
                      onClick={shareOnFacebook}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Facebook size={16} className="text-blue-600" />
                      Facebook
                    </button>
                    
                    {/* Twitter */}
                    <button
                      onClick={shareOnTwitter}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Twitter size={16} className="text-blue-400" />
                      Twitter
                    </button>
                    
                    {/* WhatsApp */}
                    <button
                      onClick={shareOnWhatsApp}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <MessageCircle size={16} className="text-green-600" />
                      WhatsApp
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* -----------------------Right Side--------------------- */}
        <div className="w-full md:w-[652px] flex  sm:flex-row justify-center items-center gap-3 sm:gap-5 mt-4 md:mt-0  sm:px-5 py-2 sm:py-4 bg-darkGraybg rounded-md border shadow-md">
          {previewCar ? (
            // Left Arrow
            <div className="flex items-center gap-2">
              <div 
                onClick={handlePrev}
                className={`w-[50px] sm:w-[64px] h-[50px] sm:h-[64px] border-[1px] flex justify-center items-center  rounded-md border-[#D6D6D6] bg-white ${previewIndex > 0 ? 'cursor-pointer hover:bg-gray-100' : 'cursor-not-allowed opacity-50'}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="25"
                  viewBox="0 0 15 25"
                  fill="none"
                >
                  <path
                    d="M0.58 13.75L11.36 24.5C11.7 24.83 12.1 25 12.57 25C13.04 25 13.46 24.83 13.82 24.5C14.18 24.16 14.36 23.75 14.36 23.25C14.36 22.75 14.17 22.33 13.78 22L4.26 12.5L13.78 3C14.17 2.66 14.36 2.25 14.36 1.75C14.36 1.25 14.18 0.83 13.82 0.5C13.46 0.16 13.04 0 12.57 0C12.1 0 11.7 0.16 11.36 0.5L0.58 11.25C0.19 11.58 0 12 0 12.5C0 13 0.19 13.41 0.58 13.75Z"
                    fill="#414042"
                  />
                </svg>
              </div>

              <div className="flex-1 flex items-center gap-2 sm:gap-4 min-w-0">
                <img
                  src={previewCarData?.images?.[0]?.imageUrl || placeholder}
                  alt="car"
                  className="h-[60px] sm:h-[79px] w-[80px] sm:w-[106px] border-gray-100 border-[1px] rounded-md object-cover flex-shrink-0"
                />
                <div className="min-w-0">
                  <h1 className="text-[18px] sm:text-[20px] text-white font-bold truncate">
                    {previewCarData?.name}
                  </h1>
                  <div className="flex flex-col sm:flex-row gap-1 sm:gap-3">
                    <h1 className="text-[12px] sm:text-[14px] font-bold text-white">
                      Run No:{" "}
                      <span className="inline-block bg-[#f15a29] text-white px-2 py-1 rounded text-[10px] sm:text-xs font-semibold whitespace-nowrap">
                        {previewRunNo} of {allVehicles.length}
                      </span>
                    </h1>
                    <h1 className="text-[12px] sm:text-[14px] text-white font-bold align-right whitespace-nowrap">
                      Current Bid:{" "}
                      <span className="text-white">
                        {previewCarData?.startingBid}
                      </span>
                    </h1>
                  </div>
                </div>
              </div>

              <div 
                onClick={handleNext}
                className={`w-[50px] sm:w-[64px] h-[50px] sm:h-[64px] border-[1px] flex justify-center items-center   rounded-md border-[#D6D6D6] bg-white ${previewIndex < allVehicles.length - 1 ? 'cursor-pointer hover:bg-gray-100' : 'cursor-not-allowed opacity-50'}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="25"
                  viewBox="0 0 15 25"
                  fill="none"
                >
                  <path
                    d="M13.78 13.75L3 24.5C2.67 24.83 2.27 25 1.79 25C1.32 25 0.9 24.83 0.54 24.5C0.18 24.16 0 23.75 0 23.25C0 22.75 0.19 22.33 0.58 22L10.1 12.5L0.58 3C0.19 2.66 0 2.25 0 1.75C0 1.25 0.18 0.83 0.54 0.5C0.9 0.16 1.32 0 1.79 0C2.27 0 2.67 0.16 3 0.5L13.78 11.25C14.17 11.58 14.36 12 14.36 12.5C14.36 13 14.17 13.41 13.78 13.75Z"
                    fill="#242424"
                  />
                </svg>
              </div>
            </div>
          ) : (
            <div>
              <h1 className="text-[14px] sm:text-[16px] font-bold text-white">
                No next car in run
              </h1>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarHeader;
